import { useRef } from "react";
import { Link } from "react-router-dom";
import { OFFERS, offerHref, isExternalOffer, type OfferKey, type OfferSurface } from "@/lib/offers";
import { trackOfferClick, useOfferView } from "@/lib/offerTracking";
import { offerWithFounding, useFoundingOffer } from "@/lib/foundingOffer";
import { FOUNDING_OFFER_KEY } from "@/lib/foundingPrice";
import "./offer-slot.css";

/**
 * One cross-promotion card: a neutral 1px-border card with a kicker, headline,
 * one line and a button. Records offer_view and offer_click (src/lib/offerTracking.ts).
 * Renders nothing for a disabled offer. The Playbook + Close Skills card shows the founding price only while
 * create-playbook-checkout says Stripe still honours it (src/lib/foundingOffer.ts); otherwise the list price.
 */
export function OfferSlot({
  offerKey,
  surface,
  compact = false,
  className = "",
  variant,
}: {
  offerKey: OfferKey | null | undefined;
  surface: OfferSurface;
  /** Stacked layout for narrow rails and modals. */
  compact?: boolean;
  className?: string;
  /** A/B arm, logged with the events so the arms can be compared. */
  variant?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const founding = useFoundingOffer(offerKey === FOUNDING_OFFER_KEY);
  const listed = offerKey ? OFFERS[offerKey] : undefined;
  const offer = listed ? offerWithFounding(listed, founding) : undefined;
  useOfferView(ref, offer?.enabled ? offer.key : null, surface, variant);

  if (!offer || !offer.enabled) return null;

  const href = offerHref(offer.key, surface);
  const onClick = () => trackOfferClick(offer.key, surface, variant);
  const cls = `af-offer${compact ? " af-offer--compact" : ""}${className ? ` ${className}` : ""}`;

  return (
    <div ref={ref} className={cls} data-offer={offer.key}>
      <div className="af-offer__text">
        <span className="af-offer__kicker">{offer.kicker}</span>
        <h3 className="af-offer__headline">{offer.headline}</h3>
        <p className="af-offer__body">{offer.body}</p>
      </div>
      {isExternalOffer(offer.key) ? (
        <a className="af-offer__btn" href={href} target="_blank" rel="noopener noreferrer" onClick={onClick}>
          {offer.cta} <span aria-hidden="true">&rarr;</span>
        </a>
      ) : (
        <Link className="af-offer__btn" to={href} onClick={onClick}>
          {offer.cta} <span aria-hidden="true">&rarr;</span>
        </Link>
      )}
    </div>
  );
}

/**
 * The lighter placement: one sentence and a text link, for a second, quieter
 * offer under a card (the Playbook note for close roles after an apply).
 */
export function OfferLine({
  offerKey,
  surface,
  lead,
  variant,
}: {
  offerKey: OfferKey | null | undefined;
  surface: OfferSurface;
  /** A few words before the line, e.g. "For your next close:". */
  lead?: string;
  variant?: string;
}) {
  const ref = useRef<HTMLParagraphElement>(null);
  const founding = useFoundingOffer(offerKey === FOUNDING_OFFER_KEY);
  const listed = offerKey ? OFFERS[offerKey] : undefined;
  const offer = listed ? offerWithFounding(listed, founding) : undefined;
  useOfferView(ref, offer?.enabled ? offer.key : null, surface, variant);
  if (!offer || !offer.enabled) return null;
  const href = offerHref(offer.key, surface);
  const onClick = () => trackOfferClick(offer.key, surface, variant);
  const text = offer.line ?? offer.headline;
  return (
    <p ref={ref} className="af-offer-line" data-offer={offer.key}>
      {lead ? <strong>{lead} </strong> : null}
      {text}{" "}
      {isExternalOffer(offer.key) ? (
        <a href={href} target="_blank" rel="noopener noreferrer" onClick={onClick}>{offer.cta} <span aria-hidden="true">&rarr;</span></a>
      ) : (
        <Link to={href} onClick={onClick}>{offer.cta} <span aria-hidden="true">&rarr;</span></Link>
      )}
    </p>
  );
}

export default OfferSlot;
